// Shared truck data store — backed by Supabase
// All functions are async; components should await them or use the subscribe pattern.

import { supabase } from '../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const toApp = (row) => ({
  id: row?.id,
  vehicleNumber: row?.vehicle_number,
  capacityMT: Number(row?.capacity_mt) || 20,
  currentLocation: row?.current_location || '',
  state: row?.state || '',
  ratePerKg: Number(row?.rate_per_kg) || 1.2,
  ratePerTon: Number(row?.rate_per_ton) || 1200,
  status: row?.status || 'Available',
  verified: row?.verified || false,
  agent_name: row?.transport_agents?.agent_name || row?.agent_name || null,
  agent_mobile: row?.transport_agents?.mobile || row?.agent_mobile || null,
});

const toDb = (app) => ({
  vehicle_number: (app?.vehicleNumber || '')?.toUpperCase(),
  capacity_mt: Number(app?.capacityMT) || 20,
  current_location: app?.currentLocation || '',
  state: app?.state || (app?.currentLocation ? app?.currentLocation?.split(',')?.[1]?.trim() || '' : ''),
  rate_per_kg: Number(app?.ratePerKg) || (app?.ratePerTon ? app?.ratePerTon / 1000 : 1.2),
  rate_per_ton: Number(app?.ratePerTon) || (app?.ratePerKg ? Math.round(app?.ratePerKg * 1000) : 1200),
  status: app?.status || 'Available',
  verified: app?.verified || false,
});

// ─── Listeners ────────────────────────────────────────────────────────────────
let listeners = [];

const notifyListeners = (data) => {
  listeners?.forEach((fn) => fn(data));
};

export const subscribeTrucks = (fn) => {
  listeners?.push(fn);

  // Immediately fetch and notify
  getTrucks()?.then((data) => fn(data));

  // Subscribe to Supabase real-time changes
  const channel = supabase?.channel('trucks_changes')?.on('postgres_changes', { event: '*', schema: 'public', table: 'trucks' }, async () => {
      const updated = await getTrucks();
      notifyListeners(updated);
    })?.subscribe();

  return () => {
    listeners = listeners?.filter((l) => l !== fn);
    supabase?.removeChannel(channel);
  };
};

// ─── CRUD ─────────────────────────────────────────────────────────────────────
export const getTrucks = async () => {
  const { data, error } = await supabase?.from('trucks')?.select(`
    *,
    transport_agents (
      agent_name,
      mobile
    )
  `)?.order('created_at', { ascending: true });
  if (error) {
    console.error('getTrucks error:', error?.message);
    return [];
  }
  return (data || [])?.map(toApp);
};

export const addTruck = async (truck) => {
  const { data, error } = await supabase?.from('trucks')?.insert(toDb(truck))?.select()?.single();
  if (error) {
    console.error('addTruck error:', error?.message);
    return null;
  }
  const newTruck = toApp(data);
  const all = await getTrucks();
  notifyListeners(all);
  return newTruck;
};

export const setTruckStatus = async (truckId, status) => {
  const { error } = await supabase?.from('trucks')?.update({ status })?.eq('id', truckId);
  if (error) {
    console.error('setTruckStatus error:', error?.message);
    return;
  }
  const all = await getTrucks();
  notifyListeners(all);
};

export const updateTruck = async (truckId, updates) => {
  const dbUpdates = {};
  if (updates?.vehicleNumber !== undefined) dbUpdates.vehicle_number = updates?.vehicleNumber?.toUpperCase();
  if (updates?.capacityMT !== undefined) dbUpdates.capacity_mt = Number(updates?.capacityMT);
  if (updates?.currentLocation !== undefined) {
    dbUpdates.current_location = updates?.currentLocation;
    dbUpdates.state = updates?.currentLocation?.split(',')?.[1]?.trim() || '';
  }
  if (updates?.ratePerTon !== undefined) {
    dbUpdates.rate_per_ton = Number(updates?.ratePerTon);
    dbUpdates.rate_per_kg = Number(updates?.ratePerTon) / 1000;
  }
  if (updates?.ratePerKg !== undefined) {
    dbUpdates.rate_per_kg = Number(updates?.ratePerKg);
    dbUpdates.rate_per_ton = Math.round(Number(updates?.ratePerKg) * 1000);
  }
  if (updates?.status !== undefined) dbUpdates.status = updates?.status;
  if (updates?.verified !== undefined) dbUpdates.verified = updates?.verified;

  const { error } = await supabase?.from('trucks')?.update(dbUpdates)?.eq('id', truckId);
  if (error) {
    console.error('updateTruck error:', error?.message);
    return;
  }
  const all = await getTrucks();
  notifyListeners(all);
};

export const removeTruck = async (truckId) => {
  const { error } = await supabase?.from('trucks')?.delete()?.eq('id', truckId);
  if (error) {
    console.error('removeTruck error:', error?.message);
    return;
  }
  const all = await getTrucks();
  notifyListeners(all);
};

// Upsert by vehicle_number — used by CSV upload
export const upsertTruckByVehicleNumber = async (truck) => {
  const { data, error } = await supabase?.from('trucks')?.upsert(toDb(truck), { onConflict: 'vehicle_number' })?.select()?.single();
  if (error) {
    console.error('upsertTruckByVehicleNumber error:', error?.message);
    return null;
  }
  const all = await getTrucks();
  notifyListeners(all);
  return toApp(data);
};
